import React, { useEffect, useMemo } from 'react';
import {
  CapacityComponent,
  Point,
  PointFlowDirection,
  SegmentFlowDirection,
} from '@/types';
import { PointBadge } from '../helpers/PointBadge';
import { useGetTSPsQuery } from '../services/edgeApi';
import { useSelector } from 'react-redux';
import { selectAllPoints, selectTsps } from '../map/slice/mapSelectors';
import { useTheme } from '../helpers/hooks';
import { BadgeDisplay } from '../helpers/BadgeDisplay';

export type CapacityComponentFormProps = {
  capacityComponent: CapacityComponent | null;
  onSave: (component: Partial<CapacityComponent>) => void;
  onCancel: () => void;
};

const BLANK_CAPACITY_COMPONENT: Partial<CapacityComponent> = {
  id: 'new',
  identifier: '',
  description: '',
  allow_displacement: false,
  points: [],
};

const FLOW_DIRECTIONS: {
  POINT: Record<PointFlowDirection, string>;
  SEGMENT: Record<SegmentFlowDirection, string>;
  DEFAULT: { '': 'N/A' };
} = {
  POINT: {
    RECEIPT: 'Receipt',
    DELIVERY: 'Delivery',
    BIDIRECTIONAL: 'Bidirectional',
    CALCULATED: 'Calculated',
    UNKNOWN: 'Unknown',
  },
  SEGMENT: {
    FORWARDHAUL: 'Forwardhaul',
    BACKHAUL: 'Backhaul',
    BIDIRECTIONAL: 'Bidirectional',
    CALCULATED: 'Calculated',
  },
  DEFAULT: {
    '': 'N/A',
  },
};

export const CapacityComponentForm: React.FC<CapacityComponentFormProps> = ({
  capacityComponent,
  onSave,
  onCancel,
}) => {
  useGetTSPsQuery(undefined);
  const [theme] = useTheme();
  const tsps = useSelector(selectTsps);
  const allPoints = useSelector(selectAllPoints);

  const [payload, setPayload] = React.useState<Partial<CapacityComponent>>(
    capacityComponent ? { ...capacityComponent } : BLANK_CAPACITY_COMPONENT,
  );

  const points = useMemo<Point[]>(
    () => capacityComponent?.points.map((pointId) => allPoints[pointId]) ?? [],
    [allPoints, capacityComponent],
  );

  const reset = () => {
    setPayload(
      capacityComponent ? { ...capacityComponent } : BLANK_CAPACITY_COMPONENT,
    );
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    const isChecked = (e.target as HTMLInputElement).checked ?? false;
    const value = isCheckbox ? isChecked : e.target.value;
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (capacityComponent) {
      setPayload({ ...capacityComponent });
    } else {
      setPayload(BLANK_CAPACITY_COMPONENT);
    }
  }, [capacityComponent]);

  return (
    <div>
      <small className="text-muted">Required fields *</small>
      <hr />
      <form className="container d-grid gap-4">
        <div className="row">
          <div className="col-4">
            <label className="form-label" htmlFor="identifier">
              Identifier
            </label>
            <input
              id="identifier"
              type="text"
              className="form-control"
              name="identifier"
              placeholder="Identifier"
              value={payload.identifier ?? ''}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <label className="form-label" htmlFor="description">
              Description *
            </label>
            <input
              id="description"
              type="text"
              className="form-control"
              name="description"
              placeholder="Description"
              value={payload.description ?? ''}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <label className="form-label" htmlFor="tsp_id">
              TSP *
            </label>
            <select
              id="tsp_id"
              className="form-select"
              name="tsp_id"
              value={payload.tsp_id ?? ''}
              onChange={handleChange}
              disabled={(capacityComponent?.points?.length ?? 0) > 0}
            >
              <option value="" disabled>
                Select TSP
              </option>
              {Object.entries(tsps).map(([id, tsp]) => (
                <option key={id} value={id}>
                  {tsp.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <label className="form-label" htmlFor="capacity_type">
              Capacity Type *
            </label>
            <select
              id="capacity_type"
              className="form-select"
              name="capacity_type"
              placeholder="Select Type"
              value={payload.capacity_type}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Type
              </option>
              <option value="POINT">Point</option>
              <option value="SEGMENT">Segment</option>
            </select>
          </div>
          <div className="col-6">
            <label className="form-label" htmlFor="flow_direction">
              Flow Direction
            </label>
            <select
              id="flow_direction"
              disabled={!payload.capacity_type}
              className="form-select"
              name="flow_direction"
              value={payload.flow_direction ?? ''}
              onChange={handleChange}
            >
              {payload.capacity_type && (
                <option value="" disabled>
                  Select Direction
                </option>
              )}
              {Object.entries(
                FLOW_DIRECTIONS[payload.capacity_type ?? 'DEFAULT'],
              ).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-5">
            <label className="form-label" htmlFor="points">
              Points
            </label>
            <div className="d-flex flex-column gap-2">
              {points.map((point) => (
                <PointBadge
                  key={point?.id.toString()}
                  point={point}
                  theme={theme}
                />
              ))}
              {points.length === 0 && (
                <span className="text-muted">No points assigned</span>
              )}
            </div>
          </div>
          <div className="col-5">
            <label className="form-label">Notice Components</label>
            <div className="d-flex flex-column gap-2">
              {Object.entries(capacityComponent?.notice_components ?? {}).map(
                ([componentId, componentDescription]) => (
                  <BadgeDisplay key={componentId.toString()} theme={theme}>
                    {componentDescription}
                  </BadgeDisplay>
                ),
              )}
              {Object.keys(capacityComponent?.notice_components ?? {})
                .length === 0 && (
                <span className="text-muted">No notice components</span>
              )}
            </div>
          </div>
          <div className="col-2 d-flex flex-column gap-1">
            <label className="form-label" htmlFor="allow_displacement">
              Allow Displacement
            </label>
            <input
              id="allow_displacement"
              type="checkbox"
              className="form-check-input"
              name="allow_displacement"
              checked={payload.allow_displacement ?? false}
              onChange={handleChange}
            />
          </div>
        </div>
      </form>
      <div className="mt-4 d-flex justify-content-end gap-3">
        <button className="btn btn-secondary" onClick={() => onSave(payload)}>
          Save
        </button>
        <button className="btn btn-secondary" onClick={reset}>
          Reset
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};
